"use client";

/**
 * Organization Context Hook
 *
 * Provides organization-specific context for the current user.
 * Uses Supabase Auth context for organization and user data.
 *
 * Architecture Rule: AU-01 - Auth Abstraction Layer
 */
import { useMemo } from "react";

import { useSupabaseAuth } from "@/components/auth/supabase-auth-provider";
import {
  DEFAULT_ORG_FEATURES,
  hasProviderAccess,
  isClinicAdmin,
  isClinicProvider,
  isClinicStaff,
} from "@/lib/utils/user-roles";
import { type OrgRole, OrgType } from "@/types/user-roles";

/**
 * Organization context returned by the hook
 */
export interface OrganizationContext {
  // Organization info
  orgId: string | null;
  orgName: string | null;
  orgSlug: string | null;

  // Environment settings
  isDemoOrg: boolean;
  zusEnvironment: "sandbox" | "production";
  drchronoEnvironment: "sandbox" | "production";

  // User's role in this org
  orgRole: OrgRole | string | null;
  isClinicAdmin: boolean;
  isClinicProvider: boolean;
  isClinicStaff: boolean;
  hasProviderAccess: boolean;

  // Platform admin (can access all orgs)
  isLyfeAdmin: boolean;

  // Features and limits
  features: string[];
  limits: {
    maxPatients: number;
    maxProviders: number;
  };

  // Branding
  branding: {
    primaryColor: string;
    secondaryColor: string;
    logo: string | null;
  };

  // Loading states
  isLoaded: boolean;
  hasOrg: boolean;
}

/**
 * Hook to get the current organization context
 * Provides environment-aware settings for data access
 */
export function useOrganizationContext(): OrganizationContext {
  const { activeOrganization, user, isLoaded } = useSupabaseAuth();

  const context = useMemo<OrganizationContext>(() => {
    if (!isLoaded) {
      return {
        orgId: null,
        orgName: null,
        orgSlug: null,
        isDemoOrg: true,
        zusEnvironment: "sandbox",
        drchronoEnvironment: "sandbox",
        orgRole: null,
        isClinicAdmin: false,
        isClinicProvider: false,
        isClinicStaff: false,
        hasProviderAccess: false,
        isLyfeAdmin: false,
        features: DEFAULT_ORG_FEATURES,
        limits: { maxPatients: 100, maxProviders: 5 },
        branding: { primaryColor: "#3B82F6", secondaryColor: "#10B981", logo: null },
        isLoaded: false,
        hasOrg: false,
      };
    }

    const isLyfeAdminUser = user?.isLyfeAdmin === true;

    // Determine if this is a demo org
    const isDemoOrg = activeOrganization?.type === OrgType.DEMO;

    // Get the user's role in the organization
    const orgRole = activeOrganization?.role || null;

    return {
      // Organization info
      orgId: activeOrganization?.id || null,
      orgName: activeOrganization?.name || null,
      orgSlug: activeOrganization?.slug || null,

      // Environment settings - demo orgs always use sandbox
      isDemoOrg,
      zusEnvironment: isDemoOrg ? "sandbox" : "production",
      drchronoEnvironment: isDemoOrg ? "sandbox" : "production",

      // User's role in this org
      orgRole,
      isClinicAdmin: isClinicAdmin(orgRole),
      isClinicProvider: isClinicProvider(orgRole),
      isClinicStaff: isClinicStaff(orgRole),
      hasProviderAccess: hasProviderAccess(orgRole),

      // Platform admin (can access all orgs)
      isLyfeAdmin: isLyfeAdminUser,

      // Features and limits (could be extended to load from org metadata)
      features: DEFAULT_ORG_FEATURES,
      limits: {
        maxPatients: 1000,
        maxProviders: 10,
      },

      // Branding (could be extended to load from org metadata)
      branding: {
        primaryColor: "#3B82F6",
        secondaryColor: "#10B981",
        logo: null,
      },

      // Loading states
      isLoaded,
      hasOrg: !!activeOrganization,
    };
  }, [activeOrganization, user, isLoaded]);

  return context;
}

/**
 * Check if a feature is enabled for the current organization
 */
export function useOrgFeature(featureName: string): boolean {
  const { features } = useOrganizationContext();
  return features.includes(featureName);
}

/**
 * Get the Zus environment for the current organization
 */
export function useZusEnvironment(): "sandbox" | "production" {
  const { zusEnvironment } = useOrganizationContext();
  return zusEnvironment;
}

/**
 * Get the DrChrono environment for the current organization
 */
export function useDrChronoEnvironment(): "sandbox" | "production" {
  const { drchronoEnvironment } = useOrganizationContext();
  return drchronoEnvironment;
}
